<svelte:options tag="pzemng-offer" immutable={true}/>

<script>
    import OfferNotPossibleWidget from './OfferNotPossibleWidget.svelte';

    // Inputs
    export let apiurl = 'https://api.pzem.online';
    export let portalurl = 'https://offerte.pzem.nl';

    // Fields
    let zipcode = {value: null, dirty: false, valid: false, error: null};
    let houseNumber = {value: null, dirty: false, valid: false, error: null};
    let houseNumberAddition = {value: null, dirty: false, valid: true, error: null};

    function showModal() {
        // Create the modal
        const modal = new OfferNotPossibleWidget({
            target: document.body,
            props: {
                zipcode: zipcode.value,
                housenumber: houseNumber.value,
                housenumberaddition: houseNumberAddition.value
            }
        });

        // When we trigger the close event, destroy the modal
        modal.$on('close', () => {
            modal.$destroy();
        })
    }

    function isRequiredFieldInvalid(value) {
        return value === null || value === '';
    }

    function invalidateFormWithError(error) {
        // Show an error in the form and set fields to invalid
        zipcode = {...zipcode, valid: false, error: error};
        houseNumber = {...houseNumber, valid: false, error: null};
    }

    function validateZipcodeField() {
        // Set field to dirty
        zipcode = {...zipcode, dirty: true};

        // Check if the required state is valid
        if(isRequiredFieldInvalid(zipcode.value)) {
            zipcode = {...zipcode, valid: false, error: 'Het postcode veld is verplicht'};
            return;
        }

        // Check if our value matches the expected value (regex)
        if(false === /^[1-9][0-9]{3}[ ]?[a-zA-Z]{2}$/.test(zipcode.value)) {
            zipcode = {...zipcode, valid: false, error: 'Het postcode veld is incorrect. Controleer de gegevens.'};
            return;
        }

        // Field is all correct
        zipcode = {...zipcode, valid: true, error: null};
    }

    function validateHouseNumberField() {
        // Set field to dirty
        houseNumber = {...houseNumber, dirty: true};

        // Check if the required state is valid
        if(isRequiredFieldInvalid(houseNumber.value)) {
            houseNumber = {...houseNumber, valid: false, error: 'Het huisnummer veld is verplicht'};
            return;
        }

        // Check if the number is negative
        if(houseNumber.value < 0) {
            houseNumber = {...houseNumber, valid: false, error: 'Het huisnummer mag niet kleiner zijn dan 0.'};
            return;
        }

        // Field is all correct
        houseNumber = {...houseNumber, valid: true, error: null};
    }

    function validateHouseNumberAdditionField() {
        // Set field to dirty
        houseNumberAddition = {...houseNumberAddition, dirty: true};
        // Check if our value matches the expected value (regex)
        if(houseNumberAddition.value && false === /^[a-zA-Z0-9\s]+$/.test(houseNumberAddition.value)) {
            houseNumberAddition = {...houseNumberAddition, valid: false, error: 'Het toevoeging veld is incorrect. Controleer de gegevens.'};
            return;
        }

        // Field is all correct
        houseNumberAddition = {...houseNumberAddition, valid: true, error: null};
    }

    function sanitizeZipcodeValue(value) {
        // Remove all whitespaces and transform to uppercase
        return value.replace(/\s/g, '').toUpperCase();
    }

    function setupUrlWithParameters(url, endpoint = false) {
        // Setup the url with parameters
        let returnUrl = url + '/'
            + ((endpoint) ? endpoint : '')
            + '?zipcode=' + sanitizeZipcodeValue(zipcode.value)
            + '&houseNumber=' + houseNumber.value;
        if(houseNumberAddition.value) {
            returnUrl += '&houseNumberAddition=' + houseNumberAddition.value;
        }

        return returnUrl;
    }

    async function fetchData() {
        // Setup the fetch url with correct parameters
        let fetchUrl = setupUrlWithParameters(apiurl, 'estate');

        // Try to fetch the data
        fetch(fetchUrl)
            .then(response => response.json())
            .then(data => {
                // Process the data
                processData(data);
            })
            .catch(error => {
                // Can't seem to get the data, show error
                invalidateFormWithError('We kunnen uw adres niet ophalen. Probeer opnieuw.');
            });
    }

    function processData(data) {
        // Check if we have any connections
        if(data.hasOwnProperty('connections')) {
            // Check if we have a large connection
            if(hasConnectionWithLargeConsumerSegment(data)) {
                // Go to the portal
                const portalUrl = setupUrlWithParameters(portalurl);
                window.open(portalUrl, '_blank');
                return;
            }

            // Show the modal
            invalidateFormWithError('Op dit adres mogen wij helaas niet leveren.');
            showModal();
            return;
        }

        // Can't find connections, show error
        invalidateFormWithError('We kunnen uw adres niet ophalen. Probeer opnieuw.');
    }

    function hasConnectionWithLargeConsumerSegment(connections) {
        // Loop through the connections
        for (const connection of connections.connections) {
            // Check if the consumerSegment is large
            if('large' === connection.consumerSegment) {
                return true;
            }
        }

        return false;
    }

    function onSubmit() {
        // Validate all fields
        validateZipcodeField();
        validateHouseNumberField();
        validateHouseNumberAdditionField();

        // Check if everything is valid
        if(true === zipcode.valid &&
            true === houseNumber.valid &&
            true === houseNumberAddition.valid) {
            // Fetch data
            fetchData();
        }
    }
</script>

<form class="form-group" on:submit|preventDefault={onSubmit}>
    <span class="form-group__title">Uw leveringsadres</span>
    <div class="form-group__widget">
        <div class="form-item">
            <label for="zipcode">Postcode</label>
            <input type="text"
                   class="form-item__input form-item__input--uppercase {(false === zipcode.valid && true === zipcode.dirty) ? 'form-item__input--invalid' : ''}"
                   id="zipcode"
                   name="zipcode"
                   placeholder="1234 AB"
                   bind:value={zipcode.value}
                   on:blur={validateZipcodeField}
            >
        </div>
        <div class="form-item">
            <label for="houseNumber">Huisnummer</label>
            <input type="number"
                   min="0"
                   class="form-item__input {(false === houseNumber.valid && true === houseNumber.dirty) ? 'form-item__input--invalid' : ''}"
                   id="houseNumber"
                   name="houseNumber"
                   placeholder="123"
                   bind:value={houseNumber.value}
                   on:blur={validateHouseNumberField}
            >
        </div>
        <div class="form-item">
            <label for="houseNumberAddition">Toevoeging</label>
            <input type="text"
                   class="form-item__input {(false === houseNumberAddition.valid && true === houseNumberAddition.dirty) ? 'form-item__input--invalid' : ''}"
                   id="houseNumberAddition"
                   name="houseNumberAddition"
                   placeholder="B"
                   bind:value={houseNumberAddition.value}
                   on:blur={validateHouseNumberAdditionField}
            >
        </div>
    </div>
    <div class="form-group__errors">
        {#if false === zipcode.valid && null !== zipcode.error }
            <span class="error">
                {zipcode.error}
            </span>
        {/if}
        {#if false === houseNumber.valid && null !== houseNumber.error }
            <span class="error">
                {houseNumber.error}
            </span>
        {/if}
        {#if false === houseNumberAddition.valid && null !== houseNumberAddition.error }
            <span class="error">
                {houseNumberAddition.error}
            </span>
        {/if}
    </div>

    <button class="button" type="submit">
        Offerte samenstellen
    </button>
</form>

<style>
    @import "@nedbase/pzem-ui/style";

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type=number] {
        -moz-appearance: textfield;
    }

    .form-group__title {
        display: block;
        margin-bottom: var(--spacing--150);
        line-height: 1.1;
        font-size: var(--font-size--xlarge);
        font-weight: var(--font-weight--bold);
        color: var(--color-body);
    }

    .form-group__widget {
        display: flex;
        flex-flow: row nowrap;
        grid-gap: var(--spacing--50);
        gap: var(--spacing--50);
    }

    .form-item {
        flex-grow: 1;
    }

    .form-item__input--uppercase {
        text-transform: uppercase;
    }

    .form-item__input--invalid {
        border-color: var(--color-error);
    }

    .error {
        display: block;
        margin: 0 0 var(--spacing--25);
        line-height: var(--line-height--small);
        font-size: var(--font-size--xxsmall);
        color: var(--color-error);
    }

    .error:first-child {
        margin-top: var(--spacing--75);
    }

    .button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        margin-top: var(--spacing--150);
        padding: var(--spacing) var(--spacing--150);
        white-space: nowrap;
        text-decoration: none;
        text-align: center;
        font-weight: var(--font-weight--semi-bold);
        color: var(--color-secondary--contrast);
        background: var(--color-secondary);
        border: 1px solid var(--color-secondary);
        border-radius: var(--border-radius--large);
        transition: all .2s ease;
        outline: none;
        cursor: pointer;
    }

    .button:focus {
        border-color: var(--color-link--focus);
    }

    .button:hover {
        color: var(--color-primary--contrast);
        background: var(--color-primary);
        border-color: var(--color-primary);
    }
</style>
